import { useTranslation } from 'react-i18next';
import { Group, Stack, Text } from '@mantine/core';
import { IconTruck } from '@tabler/icons-react';
import PartyIdentity from '@/components/app/deals/steps/PartyIdentity';
import StepDocList from '@/components/app/deals/StepDocList';
import { documentsForStep } from '@/lib/dealSteps';
import type { BargainStepsInfo } from '@/types/deals';

interface CarrierAssignSummaryProps {
  readonly info: BargainStepsInfo;
}

// Итог шага «Назначение перевозчика»: кто везёт, когда его нашли и что он подписал.
//
// Хронология из двух строк («заявка размещена» → «перевозчик взял в работу») здесь не украшение: без неё
// перевозчик и подписанная им заявка появляются в сделке внезапно, будто из ниоткуда. Документ показываем
// на самом шаге, а не только в общем дереве документов сделки, — там, где произошло действие.
//
// Реквизиты приходят не всем: продавцу сервер их не отдаёт (§9.5 плана), и блок стороны просто не
// рисуется — факт назначения он всё равно видит по строке хронологии.
export default function CarrierAssignSummary({ info }: CarrierAssignSummaryProps) {
  const { t } = useTranslation();
  const documents = documentsForStep(info.documents, 'carrierAssign');

  return (
    <Stack gap="md">
      <PartyIdentity party={info.carrier} labelKey="app.deals.carrierAssign.carrier" icon={<IconTruck size={18} />} />
      <Stack gap={4}>
        {info.carrierRequestPublishedAt ? (
          <TimelineRow label={t('app.deals.carrierAssign.publishedAt')} value={info.carrierRequestPublishedAt} />
        ) : null}
        {info.carrierAssignedAt ? (
          <TimelineRow label={t('app.deals.carrierAssign.assignedAt')} value={info.carrierAssignedAt} />
        ) : null}
      </Stack>
      <StepDocList documents={documents} />
    </Stack>
  );
}

function TimelineRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Group gap="xs" wrap="nowrap" justify="space-between">
      <Text fz="sm" c="gray.7">
        {label}
      </Text>
      <Text fz="sm" c="gray.9">
        {value}
      </Text>
    </Group>
  );
}
